/**
 * How long a manual refresh must wait before it may be sent.
 *
 * The timing decision, kept pure so it can be tested without timers or a
 * rendered tree — the hook in `./hooks` owns only the `setTimeout` around it.
 */

/**
 * Minimum gap between two refresh **requests**, measured from when the previous
 * one was sent rather than when it returned. Measuring from send is what makes
 * the guarantee simple to state — at most one request per interval — and keeps
 * it independent of how slow the endpoint happens to be.
 *
 * Five seconds because the cost of the interval falls only on someone clicking
 * again in frustration: the first click of any burst is always sent
 * immediately. The data cannot meaningfully change faster than that anyway — a
 * signal takes a minute or two to appear — so a longer gap would lose nothing
 * informationally, and is bounded instead by how long a spinner can run before
 * it reads as broken rather than busy.
 */
export const REFRESH_MIN_INTERVAL_MS = 5_000;

/**
 * Milliseconds to wait before sending a refresh; `0` means send now.
 *
 * Deliberately a throttle and not a debounce: the boundary is fixed by the
 * previous send, so clicking again never pushes it further out. A debounce
 * would let an impatient operator starve their own refresh indefinitely.
 */
export function refreshDelayMs(lastSentAt: number | null, now: number, intervalMs: number): number {
  if (lastSentAt === null) return 0;
  const elapsed = now - lastSentAt;
  return elapsed >= intervalMs ? 0 : intervalMs - elapsed;
}
